import React, { useRef, useState } from 'react'
import { Download, Paperclip, Pen, Upload, X } from 'lucide-react'
import axios from '../../config/axios'
import {
  AttachmentIcon,
  AttachmentItem,
  AttachmentList,
  AttachmentMeta,
  AttachmentSize,
  DownloadButton,
  DropZone,
  DropZoneText,
  HiddenInput,
  RemoveButton,
  SectionLabel,
  SectionWrapper,
  TitleDisplay,
  TitleInput,
} from './style'

export interface AttachmentData {
  id?: number
  file?: File
  fileUrl?: string
  title: string
  isEditing: boolean
}

interface UserAttachmentSectionProps {
  attachments: AttachmentData[]
  setAttachments: React.Dispatch<React.SetStateAction<AttachmentData[]>>
  disabled?: boolean
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const UserAttachmentSection: React.FC<UserAttachmentSectionProps> = ({
  attachments,
  setAttachments,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragActive, setIsDragActive] = useState(false)

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return
    const newItems: AttachmentData[] = Array.from(fileList).map((file) => ({
      file,
      title: file.name,
      isEditing: false,
    }))
    setAttachments((prev) => [...prev, ...newItems])
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(false)
    if (!disabled) addFiles(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled) setIsDragActive(true)
  }

  const handleDragLeave = () => setIsDragActive(false)

  const handleClick = () => {
    if (!disabled) fileInputRef.current?.click()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemove = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleStartEdit = (index: number) => {
    if (disabled) return
    setAttachments((prev) =>
      prev.map((a, i) => (i === index ? { ...a, isEditing: true } : a)),
    )
  }

  const handleTitleChange = (index: number, value: string) => {
    setAttachments((prev) =>
      prev.map((a, i) => (i === index ? { ...a, title: value } : a)),
    )
  }

  const handleSaveEdit = (index: number) => {
    setAttachments((prev) =>
      prev.map((a, i) => (i === index ? { ...a, isEditing: false } : a)),
    )
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Enter' || e.key === 'Escape') handleSaveEdit(index)
  }

  const handleDownload = async (index: number) => {
    const attachment = attachments[index]
    const fileName = attachment.title

    if (attachment.fileUrl) {
      const resp = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/file/signed-url`,
        { params: { filePath: attachment.fileUrl } },
      )
      const link = document.createElement('a')
      link.href = resp.data.url
      link.download = fileName
      link.click()
    } else if (attachment.file) {
      const objectUrl = URL.createObjectURL(attachment.file)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = fileName
      link.click()
      URL.revokeObjectURL(objectUrl)
    }
  }

  return (
    <SectionWrapper>
      <SectionLabel>Anexos</SectionLabel>

      <DropZone
        isDragActive={isDragActive}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
      >
        <Upload size={14} color="#6b7280" />
        <DropZoneText>Arraste arquivos ou clique para selecionar</DropZoneText>
        <HiddenInput
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleChange}
        />
      </DropZone>

      {attachments.length > 0 && (
        <AttachmentList>
          {attachments.map((attachment, index) => (
            <AttachmentItem key={index}>
              <AttachmentIcon>
                <Paperclip size={15} />
              </AttachmentIcon>

              <AttachmentMeta>
                {attachment.isEditing ? (
                  <TitleInput
                    autoFocus
                    value={attachment.title}
                    onChange={(e) => handleTitleChange(index, e.target.value)}
                    onBlur={() => handleSaveEdit(index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                  />
                ) : (
                  <TitleDisplay onClick={() => handleStartEdit(index)}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {attachment.title}
                    </span>
                    {!disabled && <Pen size={12} />}
                  </TitleDisplay>
                )}
                {attachment.file && (
                  <AttachmentSize>{formatSize(attachment.file.size)}</AttachmentSize>
                )}
              </AttachmentMeta>

              {(attachment.fileUrl || attachment.file) && (
                <DownloadButton
                  type="button"
                  onClick={() => handleDownload(index)}
                  title="Baixar anexo"
                >
                  <Download size={14} />
                </DownloadButton>
              )}

              {!disabled && (
                <RemoveButton
                  type="button"
                  onClick={() => handleRemove(index)}
                  title="Remover anexo"
                >
                  <X size={14} />
                </RemoveButton>
              )}
            </AttachmentItem>
          ))}
        </AttachmentList>
      )}
    </SectionWrapper>
  )
}

export default UserAttachmentSection
